import { Container } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { SkeletonData } from "@esotericsoftware/spine-pixi-v8";
import { CHARACTER_TUNING } from "../../tuning/characterTuning";
import { applyPlacement, queueClip, setClip } from "@slot/presentation";
import type { PresentationTaskRunner, SpineDriver, ViewportStore } from "@slot/presentation";

/**
 * The bear character: one Spine skeleton looping its idle; `react()` plays the reaction clip once and returns to the
 * idle. A reaction does not restart while one plays (the Cocos `CharacterAnimator`'s guard): two battles in one spin
 * make one reaction, unless the first has finished. `resetReactionState()` at each spin start lifts the guard, so a new
 * spin's battle reacts even while the last spin's reaction is still playing (autospin, the fast modes). Placed per shape
 * from `CHARACTER_TUNING`; the clips ride the chrome runner's time scale (constant perceived speed, paused and
 * frame-stepped with the game).
 */
export class CharacterView extends Container {
  private readonly skeleton: Spine;
  private readonly runner: PresentationTaskRunner;
  private readonly unsubscribe: () => void;
  private reacting = false;

  public constructor(input: { skeletonData: SkeletonData; viewport: ViewportStore; spineDriver: SpineDriver; runner: PresentationTaskRunner }) {
    super({ label: "CharacterOverlayLayer" });
    this.skeleton = new Spine({ skeletonData: input.skeletonData, autoUpdate: false });
    this.skeleton.label = "bear";
    this.skeleton.scale.set(CHARACTER_TUNING.skeletonScale);
    input.spineDriver.attach(this.skeleton);
    this.addChild(this.skeleton);
    this.runner = input.runner;
    setClip(this.skeleton, this.runner, CHARACTER_TUNING.idleClip, true);
    this.unsubscribe = input.viewport.subscribe((v) => applyPlacement(this, CHARACTER_TUNING.placement, v.shape));
  }

  /** The reaction clip once, then the idle loop again; nothing while a reaction plays. */
  public react(): void {
    if (this.reacting) return;
    const entry = setClip(this.skeleton, this.runner, CHARACTER_TUNING.reactionClip, false);
    if (!entry) return;
    this.reacting = true;
    entry.listener = {
      complete: () => {
        this.reacting = false;
      },
    };
    queueClip(this.skeleton, CHARACTER_TUNING.idleClip, true);
  }

  /** A new spin: its first battle reacts, even over the last spin's reaction. */
  public resetReactionState(): void {
    this.reacting = false;
  }

  public dispose(): void {
    this.unsubscribe();
    this.destroy({ children: true });
  }
}
